import React, { useMemo } from 'react';
import { Container, Stack, AspectRatio } from '@fiverr-private/layout_components';
import { Typography, Link } from '@fiverr-private/typography';
import { Rating } from '@fiverr-private/data_display';
import { Image } from '@fiverr-private/media';
import { VettedProBadge, TopRatedBadge, FiverrChoiceBadge, SellerLevelTwoBadge } from '@fiverr-private/badges';
import { ElementType } from '@fiverr-private/bianka_js';
import { ResponsiveHeightType, ResponsiveWidthType } from '@fiverr-private/theme/src/components/Box/types';
import { useBreakpoint } from '@fiverr-private/hooks';
import { sendBiankaClick, sendMixpanelEvent } from '../../shared/biEvents';
import { EVENT_NAMES } from '../../shared/biEvents/eventNames';
import { enrichUrlWithParams } from '../../shared/urlEnrichment';
import { GigPreview } from '../../shared/types';
import { AVATAR_IMAGE_FALLBACK } from './constants';
import {
  sellerLineClass,
  sellerLineLinkClass,
  cardTextClass,
  avatarContainerClass,
  aspectRatioContainerClass,
  overlayClass,
  descriptionClass,
} from './styles.ve.css';

interface GigPreviewCardProps extends GigPreview {
  position?: number;
  isV16?: boolean;
}

const GigPreviewCard: React.FC<GigPreviewCardProps> = ({
  coverImage,
  userName,
  rating = 4.8,
  reviewsCount = 101,
  showProBadge = true,
  showTopRatedBadge = false,
  showFiverrChoiceBadge = false,
  showSellerLevelTwoBadge = false,
  gigLink,
  sellerLink,
  avatarImage = AVATAR_IMAGE_FALLBACK,
  gigDescription,
  categoryName,
  position = 0,
  isV16 = false,
}) => {
  const breakpoint = useBreakpoint();

  const avatarSide = {
    default: '20px',
    sm: '24px',
  } as ResponsiveHeightType & ResponsiveWidthType;

  const badgesToShow = useMemo(() => {
    const badges: { type: string; component: React.ReactElement }[] = [];

    if (showProBadge) {
      badges.push({ type: 'pro', component: <VettedProBadge variant="short" /> });
    }
    if (showTopRatedBadge) {
      badges.push({ type: 'topRated', component: <TopRatedBadge /> });
    }
    if (showFiverrChoiceBadge) {
      badges.push({ type: 'fiverrChoice', component: <FiverrChoiceBadge /> });
    }
    if (showSellerLevelTwoBadge) {
      badges.push({ type: 'sellerLevelTwo', component: <SellerLevelTwoBadge /> });
    }

    const limit = breakpoint === 'lg' ? 1 : 2;

    return badges.slice(0, limit);
  }, [breakpoint, showProBadge, showTopRatedBadge, showFiverrChoiceBadge, showSellerLevelTwoBadge]);

  const enrichedGigLink = enrichUrlWithParams(gigLink, `hplo_catalog_${categoryName}`, position + 1);
  const enrichedSellerLink = enrichUrlWithParams(sellerLink, `hplo_catalog_${categoryName}`, position + 1);

  const handleCardClick = () => {
    sendBiankaClick({
      eventName: EVENT_NAMES.BIANKA.CLICKED_ON_CATALOG_SERVICE_CARD,
      elementName: 'catalog_service_card',
      elementType: ElementType.CARD,
      elementText: gigDescription,
      position: position + 1,
      isV16,
    });

    sendMixpanelEvent({
      eventName: EVENT_NAMES.MIXPANEL.LOHP_CLICKED_CATALOG_SERVICE_CARD_GIG,
      elementSource: categoryName,
      position: position + 1,
      clickedElement: 'card_image',
      clickedElementText: gigDescription,
      isV16,
    });
  };

  const handleSellerClick = () => {
    sendBiankaClick({
      eventName: EVENT_NAMES.BIANKA.CLICKED_ON_CATALOG_SERVICE_SELLER,
      elementName: 'catalog_service_seller',
      elementType: ElementType.BUTTON,
      elementText: userName,
      position: position + 1,
      isV16,
    });

    sendMixpanelEvent({
      eventName: EVENT_NAMES.MIXPANEL.LOHP_CLICKED_CATALOG_SERVICE_CARD_SELLER,
      elementSource: categoryName,
      position: position + 1,
      clickedElement: 'seller_title',
      clickedElementText: userName,
      isV16,
    });
  };

  return (
    <Container>
      <Link
        href={enrichedGigLink}
        decoration="none"
        userSelect="none"
        onDragStart={(e) => e.preventDefault()}
        onClick={handleCardClick}
      >
        <AspectRatio
          ratio="1.66/1"
          width="100%"
          borderRadius="3xl"
          position="relative"
          className={aspectRatioContainerClass}
        >
          <Image alt="Gig cover image" height="100%" src={coverImage} width="100%" />
          {gigDescription && (
            <>
              <Container
                position="absolute"
                top={0}
                left={0}
                width="100%"
                height="100%"
                borderRadius="3xl"
                className={overlayClass}
                opacity="0"
                transition="opacity 0.2s ease-in-out"
              />
              <Typography
                position="absolute"
                bottom={20}
                left={20}
                width="90%"
                color="white"
                size={{
                  default: 'b_md',
                  sm: 'b_lg',
                }}
                className={descriptionClass}
                opacity="0"
                transition="opacity 0.2s ease-in-out"
                zIndex="2"
              >
                {gigDescription}
              </Typography>
            </>
          )}
        </AspectRatio>
      </Link>

      <Stack
        direction="row"
        justifyContent="spaceBetween"
        alignItems="center"
        marginTop="3"
        className={sellerLineClass}
        gap="2"
        wrap="wrap"
      >
        <Link href={enrichedSellerLink} decoration="none" onClick={handleSellerClick} flexGrow="1">
          <Stack
            direction="row"
            alignItems="center"
            gap="2"
            justifyContent={{
              default: 'spaceBetween',
              lg: 'flexStart',
            }}
            className={sellerLineLinkClass}
          >
            <Stack alignItems="center" gap="2">
              <Stack alignItems="center" justifyContent="center" className={avatarContainerClass}>
                <Image
                  alt={`${userName} avatar`}
                  height={avatarSide}
                  width={avatarSide}
                  src={avatarImage}
                  borderRadius="circle"
                />
              </Stack>

              <Typography
                color="grey_1100"
                fontWeight="semibold"
                size={{
                  default: 'b_sm',
                  sm: 'b_md',
                }}
                className={cardTextClass}
              >
                {userName}
              </Typography>
            </Stack>
            {badgesToShow.map((badge) => (
              <React.Fragment key={badge.type}>{badge.component}</React.Fragment>
            ))}
          </Stack>
        </Link>

        <Rating variant="compact" score={rating} reviews={reviewsCount.toString()} />
      </Stack>
    </Container>
  );
};

export default GigPreviewCard;
